import React, { useEffect, useState } from "react";
import { vsOpsApi } from "./api";

function ProviderCard({ provider, selectedId, onSelect }) {
  const isSbp = provider.kind === "sbp";
  const isSelected = isSbp && provider.id === selectedId;
  const disabled = isSbp && !provider.configured;

  return (
    <label
      className={`vs-ops-pay-card${isSelected ? " vs-ops-pay-card--active" : ""}${
        disabled ? " vs-ops-pay-card--disabled" : ""
      }`}
    >
      {isSbp ? (
        <input
          type="radio"
          name="sbp_provider"
          value={provider.id}
          checked={isSelected}
          disabled={disabled}
          onChange={() => onSelect(provider.id)}
        />
      ) : (
        <span className="vs-ops-pay-card-badge">всегда</span>
      )}
      <span className="vs-ops-pay-card-body">
        <span className="vs-ops-pay-card-title">
          {provider.label}
          <span className="vs-ops-pay-card-kind">
            {provider.kind === "sbp" ? "Фиат" : "Крипта"}
          </span>
        </span>
        <span className="vs-ops-muted">{provider.description}</span>
        <span className="vs-ops-pay-card-meta">
          {provider.configured ? "ключи в env: да" : "ключи в env: нет"}
          {provider.kind === "sbp" && provider.pendingOpen != null
            ? ` · открытых счетов: ${provider.pendingOpen}`
            : null}
          {provider.kind === "sbp" && provider.active ? " · сейчас активен" : null}
        </span>
      </span>
    </label>
  );
}

function VsOpsPaymentProvidersDrawer({ open, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState("aurapay");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const state = await vsOpsApi.getPaymentProviders();
      setData(state);
      setSelectedId(state.activeSbpProvider || "aurapay");
    } catch (loadError) {
      setError(loadError.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
      setMessage("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const state = await vsOpsApi.setSbpPaymentProvider(selectedId);
      setData(state);
      setSelectedId(state.activeSbpProvider || selectedId);
      setMessage(
        `Активный СБП: ${state.activeSbpProvider}. Новые заказы пойдут туда; старые счета — своим шлюзом.`,
      );
      if (onSaved) {
        onSaved(state);
      }
    } catch (saveError) {
      setError(saveError.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return null;
  }

  const sbpProviders = (data?.providers || []).filter((item) => item.kind === "sbp");
  const cryptoProviders = (data?.providers || []).filter(
    (item) => item.kind === "crypto",
  );
  const dirty = selectedId !== (data?.activeSbpProvider || "aurapay");

  return (
    <div className="vs-ops-drawer-root" role="dialog" aria-modal="true">
      <button
        type="button"
        className="vs-ops-drawer-backdrop"
        aria-label="Закрыть"
        onClick={onClose}
      />
      <aside className="vs-ops-drawer-panel">
        <div className="vs-ops-drawer-head">
          <div>
            <h2 className="vs-ops-drawer-title">Платёжные системы</h2>
            <p className="vs-ops-muted">
              Выберите СБП-шлюз и сохраните — переключится сразу для новых заказов.
            </p>
          </div>
          <button type="button" className="vs-ops-btn vs-ops-btn-secondary" onClick={onClose}>
            Закрыть
          </button>
        </div>

        {loading ? <p className="vs-ops-muted">Загрузка…</p> : null}
        {error ? <p className="vs-ops-error">{error}</p> : null}
        {message ? <p className="vs-ops-success">{message}</p> : null}

        {!loading && data ? (
          <>
            <section className="vs-ops-drawer-section">
              <h3 className="vs-ops-drawer-subtitle">Фиат (один активный шлюз)</h3>
              <div className="vs-ops-pay-list">
                {sbpProviders.map((provider) => (
                  <ProviderCard
                    key={provider.id}
                    provider={provider}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                  />
                ))}
              </div>
            </section>

            {cryptoProviders.length ? (
              <section className="vs-ops-drawer-section">
                <h3 className="vs-ops-drawer-subtitle">Крипта</h3>
                <div className="vs-ops-pay-list">
                  {cryptoProviders.map((provider) => (
                    <ProviderCard
                      key={provider.id}
                      provider={provider}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            <p className="vs-ops-muted vs-ops-drawer-note">{data.note}</p>

            <div className="vs-ops-drawer-actions">
              <button
                type="button"
                className="vs-ops-btn"
                disabled={saving || !dirty}
                onClick={handleSave}
              >
                {saving ? "Сохранение…" : "Сохранить"}
              </button>
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
}

export default VsOpsPaymentProvidersDrawer;
